import { saveAs } from "file-saver";
import React, { FC, useState } from "react";
import { CiExport } from "react-icons/ci";
import { Tooltip as ReactTooltip } from "react-tooltip";
import * as XLSX from "xlsx";

type ExportRow = Record<string, unknown>;

type Props = {
  data: ExportRow[];
  fileName: string;
  /**
   * Loads the rows when clicked (e.g. every page of a list, QA batch 15);
   * `data` is then ignored. A rejection shows `loadError` in an alert.
   */
  loadData?: () => Promise<ExportRow[]>;
  loadError?: string;
};

export const exportDataToExcel = (data: ExportRow[], fileName: string) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, `${fileName}.xlsx`);
};

export const ExcelExport: FC<Props> = ({
  data,
  fileName,
  loadData,
  loadError = "Couldn't export. Try again.",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const exportToExcel = async () => {
    if (!loadData) {
      exportDataToExcel(data, fileName);
      return;
    }
    if (isLoading) return;
    setIsLoading(true);
    try {
      exportDataToExcel(await loadData(), fileName);
    } catch {
      window.alert(loadError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      onClick={() => void exportToExcel()}
      aria-busy={isLoading}
      className={isLoading ? "pointer-events-none opacity-50" : undefined}>
      <div className={"flex items-center"}>
        <CiExport
          data-tooltip-id={"tooltip-export"}
          className={"flex cursor-pointer items-center focus:outline-0"}
          color={"#7c66f0"}
          size={"25px"}
          strokeWidth={0.5}
        />
      </div>
      <ReactTooltip
        id={"tooltip-export"}
        place={"bottom"}
        content={"Export"}
        className={"z-10 !rounded-[8px] !p-2 !text-sm"}
      />
    </div>
  );
};
